import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  API,
  authHeaders,
  btnPrimary,
  btnSecondary,
  fieldStyle,
  labelStyle,
} from '../api/ticketsApi';
import { notifySuccess, notifyError } from '../../../lib/notify';

const DS = `${API}/data-sources`;

const blankRow = () => ({ value: '', label: '' });
const blankReporterRow = () => ({ value: '', label: '', contact_no: '', email: '' });

/**
 * Form-based data source editor.
 * Keeps meta + REST fields as forms; static options use a table UI
 * (with a parent-key sidebar for cascading) instead of endless chips.
 */
export default function DataSourceEditorForm({
  form,
  setForm,
  products = [],
  connections = [],
  onSave,
  onCancel,
  saving = false,
  lockProductId = null,
  hideProductSelect = false,
}) {
  const [selectedGroupIdx, setSelectedGroupIdx] = useState(0);
  const [groupSearch, setGroupSearch] = useState('');
  const [mergeMode, setMergeMode] = useState('replace');
  const [importing, setImporting] = useState(false);
  const excelRef = useRef(null);

  const mode = form._staticMode || 'flat';
  const groups = form._groups || [];
  const filteredGroupIdxs = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    return groups
      .map((g, i) => ({ g, i }))
      .filter(({ g }) => !q || String(g.key || '').toLowerCase().includes(q))
      .map(({ i }) => i);
  }, [groups, groupSearch]);

  const activeGroupIdx = filteredGroupIdxs.includes(selectedGroupIdx)
    ? selectedGroupIdx
    : (filteredGroupIdxs[0] ?? -1);
  const activeGroup = activeGroupIdx >= 0 ? groups[activeGroupIdx] : null;

  const updateMeta = (patch) => setForm({ ...form, ...patch });

  const setFlatOptions = (opts) => setForm({ ...form, _staticOptions: opts });

  const setGroups = (next) => setForm({ ...form, _groups: next });

  const updateGroup = (idx, patch) => {
    setGroups(groups.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  };

  const addGroup = () => {
    const next = [...groups, { key: '', options: [] }];
    setGroups(next);
    setSelectedGroupIdx(next.length - 1);
  };

  const removeGroup = (idx) => {
    const next = groups.filter((_, i) => i !== idx);
    setGroups(next);
    setSelectedGroupIdx(Math.max(0, Math.min(idx, next.length - 1)));
  };

  const addFlatRow = () => setFlatOptions([...(form._staticOptions || []), blankRow()]);
  const addGroupRow = () => {
    if (activeGroupIdx < 0) return;
    updateGroup(activeGroupIdx, {
      options: [...(activeGroup.options || []), mode === 'branch_reporters' ? blankReporterRow() : blankRow()],
    });
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${DS}/excel/template`, {
        ...authHeaders(),
        params: { mode },
        responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `data_source_${mode}_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      notifySuccess('Template downloaded');
    } catch (e) {
      notifyError(e.response?.data?.detail || 'Template download failed');
    }
  };

  const exportExcel = async () => {
    if (!form.data_source_id) {
      notifyError('Save the data source first, then export');
      return;
    }
    try {
      const r = await axios.get(`${DS}/${form.data_source_id}/excel/export`, {
        ...authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${form.name || form.data_source_id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      notifySuccess('Excel exported');
    } catch (e) {
      notifyError(e.response?.data?.detail || 'Excel export failed');
    }
  };

  const onExcelChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${DS}/excel/parse?mode=${mode}`, fd, authHeaders());
      applyParsed(r.data || {}, mergeMode);
      const d = r.data || {};
      if (d.errors?.length) {
        notifyError(`Loaded with ${d.errors.length} warning(s). First: ${d.errors[0]}`);
      } else {
        notifySuccess(`Loaded ${d.rows_imported || 0} row(s) into the form — click Save`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      notifyError(typeof detail === 'string' ? detail : 'Excel import failed');
    } finally {
      setImporting(false);
    }
  };

  const applyParsed = (data, merge) => {
    if (mode === 'flat') {
      const incoming = data.static_options || [];
      if (merge === 'append') {
        const byVal = {};
        (form._staticOptions || []).forEach((o) => { byVal[String(o.value || '').toLowerCase()] = o; });
        incoming.forEach((o) => { byVal[String(o.value || '').toLowerCase()] = o; });
        setFlatOptions(Object.values(byVal));
      } else {
        setFlatOptions(incoming);
      }
      return;
    }
    const incomingGroups = Object.entries(data.static_groups || {}).map(([key, options]) => ({
      key,
      options: options || [],
    }));
    if (merge === 'append') {
      const map = {};
      groups.forEach((g) => {
        const opts = {};
        (g.options || []).forEach((o) => {
          opts[String(o.value || '').toLowerCase()] = o;
        });
        map[g.key] = { key: g.key, options: opts };
      });
      incomingGroups.forEach((g) => {
        if (!map[g.key]) map[g.key] = { key: g.key, options: {} };
        (g.options || []).forEach((o) => {
          map[g.key].options[String(o.value || '').toLowerCase()] = o;
        });
      });
      setGroups(Object.values(map).map((g) => ({ key: g.key, options: Object.values(g.options) })));
    } else {
      setGroups(incomingGroups);
    }
    setSelectedGroupIdx(0);
  };

  const flatCount = (form._staticOptions || []).length;
  const groupOptionCount = groups.reduce((n, g) => n + (g.options || []).length, 0);

  return (
    <div style={shell}>
      <div style={headerBar}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
            {form.data_source_id ? 'Edit data source' : 'New data source'}
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
            Fill the form below. For large lists use Excel import instead of scrolling chips.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={btnSecondary} onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" style={btnPrimary} onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save data source'}
          </button>
        </div>
      </div>

      <section style={section}>
        <h3 style={sectionTitle}>Basics</h3>
        <div style={grid2}>
          <Field label="Name *">
            <input style={fieldStyle} value={form.name || ''} onChange={(e) => updateMeta({ name: e.target.value })} placeholder="e.g. Clients by group" />
          </Field>
          {!hideProductSelect && (
            <Field label="Product">
              <select
                style={fieldStyle}
                value={form.product_id || ''}
                disabled={Boolean(lockProductId)}
                onChange={(e) => updateMeta({ product_id: e.target.value })}
              >
                <option value="">Shared / General (any product)</option>
                {products.map((p) => (
                  <option key={p.product_id} value={p.product_id}>{p.name}</option>
                ))}
              </select>
            </Field>
          )}
          {hideProductSelect && lockProductId && (
            <Field label="Product">
              <input
                style={{ ...fieldStyle, background: '#F9FAFB' }}
                value={products.find((p) => p.product_id === lockProductId)?.name || lockProductId}
                disabled
              />
            </Field>
          )}
          <Field label="Type">
            <select style={fieldStyle} value={form.type || 'static'} onChange={(e) => updateMeta({ type: e.target.value })}>
              <option value="static">Static list</option>
              <option value="rest">REST API</option>
            </select>
          </Field>
          {form.type === 'static' && (
            <Field label="Static list mode">
              <select
                style={fieldStyle}
                value={mode}
                onChange={(e) => updateMeta({ _staticMode: e.target.value })}
              >
                <option value="flat">Flat — same list always</option>
                <option value="grouped">Grouped — cascade by parent value</option>
                <option value="branch_reporters">Branch reporters — group|company|branch</option>
              </select>
            </Field>
          )}
        </div>
      </section>

      {form.type === 'rest' && (
        <section style={section}>
          <h3 style={sectionTitle}>REST mapping</h3>
          <div style={grid2}>
            <Field label="Connection">
              <select style={fieldStyle} value={form.connection_id || ''} onChange={(e) => updateMeta({ connection_id: e.target.value })}>
                <option value="">Select…</option>
                {connections.map((c) => (
                  <option key={c.connection_id} value={c.connection_id}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Method">
              <select style={fieldStyle} value={form.method || 'GET'} onChange={(e) => updateMeta({ method: e.target.value })}>
                <option>GET</option>
                <option>POST</option>
              </select>
            </Field>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="URL template (use {{field_key}} for parent values)">
                <input
                  style={fieldStyle}
                  value={form.url_template || ''}
                  placeholder="/districts?state_id={{state}}"
                  onChange={(e) => updateMeta({ url_template: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Array path">
              <input style={fieldStyle} value={form.array_path || ''} placeholder="data" onChange={(e) => updateMeta({ array_path: e.target.value })} />
            </Field>
            <Field label="Value key">
              <input style={fieldStyle} value={form.value_key || ''} placeholder="id" onChange={(e) => updateMeta({ value_key: e.target.value })} />
            </Field>
            <Field label="Label key">
              <input style={fieldStyle} value={form.label_key || ''} placeholder="name" onChange={(e) => updateMeta({ label_key: e.target.value })} />
            </Field>
          </div>
        </section>
      )}

      {form.type === 'static' && (
        <section style={section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ ...sectionTitle, marginBottom: 4 }}>Options</h3>
              <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>
                {mode === 'flat'
                  ? `${flatCount} option(s) — edit in the table or import Excel`
                  : `${groups.length} parent group(s) · ${groupOptionCount} option(s)`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                style={{ ...fieldStyle, width: 'auto', minWidth: 120, margin: 0 }}
                value={mergeMode}
                onChange={(e) => setMergeMode(e.target.value)}
                title="How Excel rows merge into the form"
              >
                <option value="replace">Import: replace</option>
                <option value="append">Import: append</option>
              </select>
              <button type="button" style={btnSecondary} onClick={downloadTemplate}>Download template</button>
              <button type="button" style={btnSecondary} onClick={() => excelRef.current?.click()} disabled={importing}>
                {importing ? 'Importing…' : 'Import Excel'}
              </button>
              {form.data_source_id ? (
                <button type="button" style={btnSecondary} onClick={exportExcel}>Export Excel</button>
              ) : null}
              <input
                ref={excelRef}
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: 'none' }}
                onChange={onExcelChosen}
              />
            </div>
          </div>

          {mode === 'flat' ? (
            <OptionsTable
              columns={[
                { key: 'value', label: 'Value *', placeholder: 'retail' },
                { key: 'label', label: 'Label', placeholder: 'Retail' },
              ]}
              rows={form._staticOptions || []}
              onChange={setFlatOptions}
              onAdd={addFlatRow}
              blank={blankRow}
            />
          ) : (
            <div style={split}>
              <aside style={sidebar}>
                <input
                  style={{ ...fieldStyle, marginBottom: 8 }}
                  placeholder="Search parent keys…"
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                />
                <div style={sidebarList}>
                  {filteredGroupIdxs.length === 0 && (
                    <div style={{ fontSize: 12, color: '#9CA3AF', padding: 8 }}>No groups yet</div>
                  )}
                  {filteredGroupIdxs.map((idx) => {
                    const g = groups[idx];
                    const active = idx === activeGroupIdx;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedGroupIdx(idx)}
                        style={{
                          ...sideItem,
                          background: active ? '#EFF6FF' : 'transparent',
                          borderColor: active ? '#BFDBFE' : 'transparent',
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 12, color: '#111827', wordBreak: 'break-all' }}>
                          {g.key || <em style={{ color: '#9CA3AF', fontWeight: 400 }}>(empty key)</em>}
                        </div>
                        <div style={{ fontSize: 11, color: '#6B7280' }}>{(g.options || []).length} options</div>
                      </button>
                    );
                  })}
                </div>
                <button type="button" style={{ ...btnSecondary, width: '100%', marginTop: 8 }} onClick={addGroup}>
                  + Add {mode === 'branch_reporters' ? 'branch key' : 'parent value'}
                </button>
              </aside>

              <div style={{ minWidth: 0, flex: 1 }}>
                {!activeGroup ? (
                  <div style={emptyPane}>
                    Add a parent value on the left, then enter options in the table.
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>
                          {mode === 'branch_reporters' ? 'Group | Company | Branch' : 'When parent value ='}
                        </label>
                        <input
                          style={fieldStyle}
                          value={activeGroup.key || ''}
                          placeholder={mode === 'branch_reporters' ? 'KAPIL GROUP|AVP KAPIL|VIJAYAWADA' : 'retail'}
                          onChange={(e) => updateGroup(activeGroupIdx, { key: e.target.value })}
                        />
                      </div>
                      <button
                        type="button"
                        style={{ ...btnSecondary, color: '#DC2626' }}
                        onClick={() => removeGroup(activeGroupIdx)}
                      >
                        Remove group
                      </button>
                    </div>
                    <OptionsTable
                      columns={mode === 'branch_reporters'
                        ? [
                          { key: 'value', label: 'Name *', placeholder: 'Ravi Kumar' },
                          { key: 'contact_no', label: 'Phone', placeholder: '9876543210' },
                          { key: 'email', label: 'Email', placeholder: 'ravi@example.com' },
                        ]
                        : [
                          { key: 'value', label: 'Value *', placeholder: 'acme' },
                          { key: 'label', label: 'Label', placeholder: 'Acme Corp' },
                        ]}
                      rows={activeGroup.options || []}
                      onChange={(opts) => updateGroup(activeGroupIdx, {
                        options: mode === 'branch_reporters'
                          ? opts.map((o) => ({ ...o, label: o.label || o.value }))
                          : opts,
                      })}
                      onAdd={addGroupRow}
                      blank={mode === 'branch_reporters' ? blankReporterRow : blankRow}
                    />
                  </>
                )}
              </div>
            </div>
          )}

          <p style={{ fontSize: 11, color: '#9CA3AF', margin: '10px 0 0' }}>
            Excel columns — flat: VALUE, LABEL · grouped: PARENT_VALUE, VALUE, LABEL · reporters: GROUP, COMPANY, BRANCH, NAME, PHONE, EMAIL
          </p>
        </section>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function OptionsTable({ columns, rows, onChange, onAdd, blank }) {
  const updateCell = (idx, key, value) => {
    onChange(rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div style={{ marginTop: 12 }}>
      <div style={tableWrap}>
        <table style={table}>
          <thead>
            <tr>
              <th style={{ ...th, width: 44 }}>#</th>
              {columns.map((c) => (
                <th key={c.key} style={th}>{c.label}</th>
              ))}
              <th style={{ ...th, width: 72 }} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 2} style={{ ...td, color: '#9CA3AF', textAlign: 'center' }}>
                  No rows yet — add a row or import Excel
                </td>
              </tr>
            )}
            {rows.map((row, idx) => (
              <tr key={idx}>
                <td style={{ ...td, color: '#9CA3AF', fontSize: 11 }}>{idx + 1}</td>
                {columns.map((c) => (
                  <td key={c.key} style={td}>
                    <input
                      style={cellInput}
                      value={row[c.key] ?? ''}
                      placeholder={c.placeholder}
                      onChange={(e) => updateCell(idx, c.key, e.target.value)}
                    />
                  </td>
                ))}
                <td style={td}>
                  <button type="button" style={linkDanger} onClick={() => removeRow(idx)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" style={{ ...btnSecondary, marginTop: 8 }} onClick={onAdd}>+ Add row</button>
      {typeof blank === 'function' ? null : null}
    </div>
  );
}

const shell = {
  border: '1px solid #E5E7EB',
  borderRadius: 14,
  background: '#fff',
  overflow: 'hidden',
  marginTop: 12,
};
const headerBar = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  alignItems: 'center',
  flexWrap: 'wrap',
  padding: '14px 16px',
  borderBottom: '1px solid #E5E7EB',
  background: '#F8FAFC',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};
const section = {
  padding: '16px',
  borderBottom: '1px solid #F3F4F6',
};
const sectionTitle = {
  margin: '0 0 12px',
  fontSize: 13,
  fontWeight: 700,
  color: '#111827',
};
const grid2 = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};
const split = {
  display: 'grid',
  gridTemplateColumns: '240px 1fr',
  gap: 12,
  marginTop: 12,
  minHeight: 320,
};
const sidebar = {
  border: '1px solid #E5E7EB',
  borderRadius: 10,
  padding: 10,
  background: '#F9FAFB',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};
const sidebarList = {
  overflowY: 'auto',
  maxHeight: 360,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  flex: 1,
};
const sideItem = {
  textAlign: 'left',
  border: '1px solid transparent',
  borderRadius: 8,
  padding: '8px 10px',
  cursor: 'pointer',
  background: 'transparent',
};
const emptyPane = {
  border: '1px dashed #D1D5DB',
  borderRadius: 10,
  padding: 28,
  textAlign: 'center',
  color: '#9CA3AF',
  fontSize: 13,
};
const tableWrap = {
  border: '1px solid #E5E7EB',
  borderRadius: 10,
  overflow: 'auto',
  maxHeight: 380,
};
const table = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};
const th = {
  position: 'sticky',
  top: 0,
  background: '#F3F4F6',
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 11,
  fontWeight: 700,
  color: '#4B5563',
  borderBottom: '1px solid #E5E7EB',
  zIndex: 1,
};
const td = {
  padding: '4px 8px',
  borderBottom: '1px solid #F3F4F6',
  verticalAlign: 'middle',
};
const cellInput = {
  width: '100%',
  border: '1px solid transparent',
  borderRadius: 6,
  padding: '7px 8px',
  fontSize: 13,
  background: 'transparent',
  outline: 'none',
};
const linkDanger = {
  border: 'none',
  background: 'transparent',
  color: '#DC2626',
  fontSize: 12,
  cursor: 'pointer',
  padding: '4px 6px',
};
